import { getProjectTimezone } from '@/utils/projectContext'

/**
 * 화면 표시용 TZ.
 * explicit → projectContext.timezone → 브라우저 로컬(undefined)
 */
export function resolveDisplayTimeZone(explicit?: string | null): string | undefined {
  const fromExplicit = explicit?.trim()
  if (fromExplicit) return fromExplicit
  return getProjectTimezone() ?? undefined
}

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? ''
}

function buildOptions(
  withTime: boolean,
  timeZone?: string
): Intl.DateTimeFormatOptions {
  return {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime
      ? {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23' as const
        }
      : {}),
    ...(timeZone ? { timeZone } : {})
  }
}

function formatParts(date: Date, withTime: boolean, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', buildOptions(withTime, timeZone)).formatToParts(
    date
  )
  const y = partValue(parts, 'year')
  const m = partValue(parts, 'month')
  const d = partValue(parts, 'day')
  if (!withTime) return `${y}.${m}.${d}`
  const hh = partValue(parts, 'hour')
  const mm = partValue(parts, 'minute')
  const ss = partValue(parts, 'second')
  return `${y}.${m}.${d} ${hh}:${mm}:${ss}`
}

function formatIsoInTimeZone(
  isoString: string,
  withTime: boolean,
  explicitTimeZone?: string | null
): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return isoString

  const timeZone = resolveDisplayTimeZone(explicitTimeZone)
  try {
    return formatParts(date, withTime, timeZone)
  } catch {
    // 잘못된 IANA TZ 등이면 브라우저 로컬로 폴백
    if (!timeZone) return isoString
    try {
      return formatParts(date, withTime, undefined)
    } catch {
      return isoString
    }
  }
}

/** ISO 8601 → YYYY.MM.DD (project timezone, 없으면 로컬) */
export function formatYmdDot(isoString: string, timeZone?: string | null): string {
  return formatIsoInTimeZone(isoString, false, timeZone)
}

/** ISO 8601 → YYYY.MM.DD HH:mm:ss (project timezone, 없으면 로컬) */
export function formatYmdHms(isoString: string, timeZone?: string | null): string {
  return formatIsoInTimeZone(isoString, true, timeZone)
}

function todayStringLocal(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * 오늘 날짜 YYYY-MM-DD.
 * explicit → projectContext.timezone → 브라우저 로컬
 */
export function getTodayString(explicitTimeZone?: string | null): string {
  const timeZone = resolveDisplayTimeZone(explicitTimeZone)
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timeZone ? { timeZone } : {})
    }).formatToParts(new Date())
    const y = partValue(parts, 'year')
    const m = partValue(parts, 'month')
    const d = partValue(parts, 'day')
    if (y && m && d) return `${y}-${m}-${d}`
  } catch {
    // 잘못된 IANA TZ 등이면 로컬로 폴백
  }
  return todayStringLocal()
}
